/* eslint-disable max-len */
import { Gender } from '../../businessLogic/AssessmentQuestionTypes';
import * as images from '../../components/assets/Images';
import * as icons from '../../components/assets/images/Icons';
import { routeBy } from '../../components/flows/FlowNavigation';
import { AuthLayoutConfig } from '../../components/flows/layouts/AuthLayout';
import {
  getAuthMethodUsed,
  getIsWearableProvided,
  getMedicareAccessStateRoute,
  getPaymentPathRoute,
} from '../../util/ConditionalNextScreenSelectors';
import {
  getReferredCoverageEntryRoute,
  getReferredCoverageResultsRoute,
  getReferredPartnerInsurerAccessRoute,
} from '../../util/CoverageSelectors';
import { getAslPaymentLink } from '../ASL';
import {
  CoverageScreenName,
  FlowConfig,
  MedicareAccessScreenName,
  PapScreenName,
  ScreenType,
  SharedScreenName,
} from '../FlowTypes';
import {
  AU_COVERAGE_CHECK_LAYOUT,
  COVERAGE_CHECK_LAYOUT,
  COVERAGE_RESULTS_LAYOUT,
  COVERAGE_SCREENS,
  PARTNER_INSURER_ACCESS_LAYOUT,
} from './CoverageConfig';
import { MEDICARE_ACCESS_SCREENS, MEDICARE_ACCESS_STATE_LAYOUT } from './MedicareAccessConfig';
import { NEXT_BUTTON_TITLE, NervaControlScreen, NervaControlScreenName } from './NervaControl';
import { PATIENT_ASSISTANCE_SCREENS } from './PapConfig';

/**
 * Shared by both "you're all set" screens. The trial-expiry detail only renders when the user
 * actually has a trial, so it stays hidden on the referral screen where access is already granted.
 */
const DOWNLOAD_APP_STEPS = [
  {
    title: 'Download the Nerva app',
    description: 'Search for Nerva in the App Store or Google Play.',
  },
  {
    title: 'Sign in',
    description: 'Use the same email address and password you just created.',
    detail: 'email' as const,
  },
  {
    title: 'Start your first session',
    description: 'Open the app, press play and we’ll guide you from there.',
    detail: 'trialExpiry' as const,
  },
];

const DOWNLOAD_APP_HELP = {
  title: 'Need help getting started?',
  subtitle: 'Our team is here to help.',
  items: ['✉️ nerva@mindsethealth.com', '💬 Message us through the in-app chat'],
};

const authLayout: AuthLayoutConfig = {
  registerTitle: 'Create your secure account',
  registerBody: [
    'Save your answers and view your program next. Signing up is free and nothing is charged now.',
  ],
  registerPrivacyDeclaration: ['Your progress is only shared with your healthcare professional.'],
  privacyLogos: [
    { src: images.hipaaLogo, altText: 'HIPAA compliant' },
    { src: images.soc2Logo, altText: 'SOC 2 certified' },
  ],
  registerButtonTitle: 'View your program',
  loginTitle: 'Welcome back!',
  loginButtonTitle: 'View your program',
};

export const NERVA_HCP_REFERRED_PERSONALISE_CONFIG: FlowConfig<NervaControlScreen> = {
  name: 'NervaHCPReferredPersonalise',
  showProgressBar: true,
  startEvent: undefined,
  screens: [
    {
      screenName: NervaControlScreenName.WelcomeHcpReferred,
      type: ScreenType.HcpReferred,
      layout: {
        title: 'Living with digestive symptoms can be exhausting',
        content: [
          {
            type: 'text',
            content:
              'No matter how long you’ve been dealing with this, your symptoms are real and physical. And if you’ve ever felt unheard or left without clear answers, you’re in the right place.',
          },
          {
            type: 'text',
            content:
              'Everyone arrives with a different story – different symptoms, histories, and diagnoses – so there are no right or wrong answers.',
          },
          {
            type: 'text',
            content: 'Just answer as honestly as you can, and we’ll shape the program around you.',
          },
          {
            type: 'image',
            src: images.nervaRatings,
            fullWidth: true,
            altText: 'Nerva ratings',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.DateOfBirth,
    },

    {
      screenName: SharedScreenName.DateOfBirth,
      type: ScreenType.DateOfBirth,
      layout: {
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        infoBox: {
          titleText: 'Why do we ask this?',
          bodyText:
            'Your date of birth helps your healthcare provider identify you. Your information is private and secure.',
        },
      },
      nextScreen: SharedScreenName.Gender,
    },

    {
      screenName: SharedScreenName.Gender,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['What is your gender?'],
        choices: [Gender.Male, Gender.Female, Gender.Nonbinary, Gender.Other, Gender.Unstated],
      },
      nextScreen: NervaControlScreenName.LowerGISymptoms,
    },

    {
      screenName: NervaControlScreenName.LowerGISymptoms,
      type: ScreenType.MultiSelect,
      layout: {
        body: [
          'Which of these physical symptoms do you experience?',
          'Your program is personalized based on your symptoms',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Diarrhea',
          'Constipation',
          'Abdominal pain',
          'Bloating',
          { label: 'Gas/Wind', value: 'Wind' },
          'Nausea',
          'More/Other',
        ],
        toastMessage: 'Added to your program',
        toastDuration: 1000,
      },
      nextScreen: NervaControlScreenName.UpperGISymptoms,
    },

    {
      screenName: NervaControlScreenName.UpperGISymptoms,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['Do you experience any other gastrointestinal symptoms?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Heartburn',
          // Clarifiers are display-only: the stored values stay `Dyspepsia` and `Dysphagia` so
          // answers already collected keep their meaning.
          { label: 'Dyspepsia (indigestion)', value: 'Dyspepsia' },
          'Regurgitation',
          { label: 'Belching/Burping', value: 'Burping' },
          { label: 'Dysphagia (trouble swallowing)', value: 'Dysphagia' },
          'More/Other',
          'None',
        ],
        exclusiveChoices: ['None'],
      },
      nextScreen: NervaControlScreenName.GutBrainDiagnosis,
    },

    {
      screenName: NervaControlScreenName.GutBrainDiagnosis,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['Have you been diagnosed with any of these?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        exclusiveChoices: ['None'],
        choices: [
          'Irritable Bowel Syndrome (IBS)',
          'Functional Dyspepsia',
          'Rumination Syndrome',
          'Functional Heartburn',
          'Functional Dysphagia',
          'Other',
          'None',
        ],
        infoBox: {
          bodyText:
            'Not sure, or no diagnosis? That’s okay — many people start here without one. These are conditions where symptoms occur without structural damage to the gut (gut-brain disorders).',
        },
      },
      nextScreen: {
        'Irritable Bowel Syndrome (IBS)': SharedScreenName.Classify,
        fallback: NervaControlScreenName.StructuralConditions,
      },
    },

    {
      screenName: SharedScreenName.Classify,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Based on your symptoms, which type of IBS fits you best?'],
        choices: ['IBS-C (Constipation)', 'IBS-D (Diarrhea)', 'IBS-M (Mixed)', 'I’m not sure'],
      },
      nextScreen: NervaControlScreenName.StructuralConditions,
    },

    {
      screenName: NervaControlScreenName.StructuralConditions,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['Have you been diagnosed with any of the following digestive conditions?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Inflammatory Bowel Disease (IBD)',
          'Small Intestinal Bacterial Overgrowth (SIBO)',
          'Gastro-oesophageal reflux disease (GERD)',
          'Celiac disease',
          'Barrett’s oesophagus',
          'More/Other',
          'None',
        ],
        exclusiveChoices: ['None'],
        infoBox: {
          bodyText: 'Not sure, or no diagnosis? That’s okay — many people start here without one.',
        },
      },
      nextScreen: {
        'Inflammatory Bowel Disease (IBD)': NervaControlScreenName.IBDSubtype,
        fallback: NervaControlScreenName.Triggers,
      },
    },

    {
      screenName: NervaControlScreenName.IBDSubtype,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['What IBD subtype have you been diagnosed with?'],
        choices: ['Crohn’s disease', 'Ulcerative colitis', 'I’m not sure'],
      },
      nextScreen: NervaControlScreenName.Triggers,
    },

    {
      screenName: NervaControlScreenName.Triggers,
      type: ScreenType.MultiSelect,
      layout: {
        body: [
          'Which of these tend to affect your symptoms?',
          'Many people notice that certain things seem to make their symptoms worse.',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Food',
          'Anxiety/Stress',
          'Medication',
          'Exercise',
          'Fatigue',
          'Other',
          'I’m not sure',
        ],
      },
      nextScreen: NervaControlScreenName.ImpactLife,
    },

    {
      screenName: NervaControlScreenName.ImpactLife,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['How have your symptoms affected your daily life?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          {
            label: 'Physically uncomfortable',
            value: 'Feeling physically uncomfortable',
          },
          'Can’t eat foods I love',
          'Nervous to socialize',
          'Worrying about travel',
          'Struggle with work',
          'More/Other',
        ],
      },
      nextScreen: NervaControlScreenName.TriggersValidation,
    },

    {
      screenName: NervaControlScreenName.TriggersValidation,
      type: ScreenType.Presentation,
      layout: {
        title: 'Your symptoms are not in your head',
        content: [
          {
            type: 'text',
            content: 'Living with gut symptoms can feel like your body is working against you.',
          },
          {
            type: 'text',
            content:
              'In gut-brain disorders, triggers like food or stress may set off symptoms – but they aren’t always the whole story.',
          },
          {
            type: 'text',
            content:
              'For many people, the gut-brain connection can become heightened, making normal digestive sensations feel stronger, more uncomfortable, or harder to predict.',
          },
          {
            type: 'image',
            src: images.brainGutArrows,
            fullWidth: true,
            altText: 'The brain and gut sending signals to each other',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: NervaControlScreenName.EducationTriggers,
    },

    {
      screenName: NervaControlScreenName.EducationTriggers,
      type: ScreenType.Presentation,
      layout: {
        title: 'Your gut may be on high alert',
        content: [
          {
            type: 'text',
            content:
              'When the gut-brain connection becomes heightened, everyday digestive signals can feel louder than they should.',
          },
          {
            type: 'text',
            content:
              'It’s like the sensitivity dial has been turned up – so normal gut sensations can feel stronger, more uncomfortable, or harder to ignore.',
          },
          {
            type: 'image',
            src: images.sensitivityDial,
            altText: 'A sensitivity dial turned up towards the red end',
            maxWidth: 220,
          },
          {
            type: 'text',
            content:
              'Nerva is designed to help calm this gut-brain response and support symptom management beyond only avoiding triggers.',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: NervaControlScreenName.IfFreeFromIbs,
    },

    {
      screenName: NervaControlScreenName.IfFreeFromIbs,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['As your symptoms calm, what would you look forward to?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          { label: 'Eating the foods I love', value: 'Eat the foods I love' },
          {
            label: 'Enjoying my social life and relationships',
            value: 'Enjoy my social life and relationships',
          },
          { label: 'Sleeping better', value: 'Sleep better' },
          { label: 'Traveling with confidence', value: 'Travel with confidence' },
          { label: 'Being more present at work', value: 'Be more present at work' },
          'More/Other',
        ],
      },
      nextScreen: NervaControlScreenName.Cotreatments,
    },

    {
      screenName: NervaControlScreenName.Cotreatments,
      type: ScreenType.MultiSelect,
      layout: {
        body: [
          'Are you currently engaging in or planning to begin any of the following treatments?',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Low FODMAP diet',
          'Neuromodulators',
          'Antispasmodics',
          'Probiotics',
          'Fibre supplements',
          'More/Other',
          'None',
        ],
      },
      nextScreen: NervaControlScreenName.EducationCotreatments,
    },

    {
      screenName: NervaControlScreenName.EducationCotreatments,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content: 'Nerva is designed to work alongside other treatments.',
          },
          {
            type: 'text',
            content:
              'Research shows people respond to gut-directed hypnotherapy at similar rates whether or not they’ve already tried medications, supplements, or dietary changes.¹',
          },
        ],
        footnotes: [
          'Peters, S.L., Yao, C.K., Philpott, H., Yelland, G.W., Muir, J.G. and Gibson, P.R. (2016). Randomised clinical trial: the efficacy of gut-directed hypnotherapy is similar to that of the low FODMAP diet for the treatment of irritable bowel syndrome. Alimentary Pharmacology & Therapeutics, 44(5), pp.447–459. doi:10.1111/apt.13706.',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      ...routeBy(getIsWearableProvided, {
        hasWearable: NervaControlScreenName.WearableBiometrics,
        fallback: SharedScreenName.Auth,
      }),
    },

    {
      screenName: NervaControlScreenName.WearableBiometrics,
      type: ScreenType.Presentation,
      layout: {
        title: 'See how your body responds to Nerva',
        content: [
          {
            type: 'text',
            content:
              'Your healthcare provider has given you a heart rate monitor to wear during your Nerva sessions.',
          },
          { type: 'image', src: images.biometrics, altText: 'heart rate monitor' },
          {
            type: 'text',
            content:
              'As you progress through the program, your nervous system will gradually learn to shift toward a calmer, more regulated state. Tracking your heart rate can help you see that process happening.',
          },
          { type: 'text', content: 'What this adds to your experience:' },
          {
            type: 'bulletItem',
            content: '**Real-time insight** into how your nervous system responds during sessions',
          },
          {
            type: 'bulletItem',
            content: '**Track your progress** as your body becomes more regulated over time',
          },
          {
            type: 'bulletItem',
            content: '**Make your improvements visible**, not just something you feel',
          },
        ],
        primaryButtonTitle: 'Continue',
      },
      nextScreen: SharedScreenName.Auth,
    },

    {
      screenName: SharedScreenName.Auth,
      type: ScreenType.Auth,
      layout: {
        ...authLayout,
      },
      ...routeBy(getAuthMethodUsed, {
        loginAndAlreadyPaid: SharedScreenName.AlreadyPaidDownloadApp,
        login: NervaControlScreenName.AiLoading,
        register: NervaControlScreenName.AiLoading,
        fallback: NervaControlScreenName.AiLoading,
      }),
    },

    {
      screenName: NervaControlScreenName.AiLoading,
      type: ScreenType.AiLoading,
      layout: {
        title: 'Personalizing your program',
        subtitle: 'This takes just a moment',
        showAiBadge: false,
        loadingBars: [
          {
            title: 'Analyzing your symptoms',
            icon: icons.heartbeat,
            startNumber: 0,
            endNumber: 100,
            durationMs: 3500,
          },
          {
            title: 'Reviewing history',
            icon: icons.document,
            startNumber: 0,
            endNumber: 100,
            durationMs: 2500,
          },
          {
            title: 'Assessing goals',
            icon: icons.target,
            startNumber: 0,
            endNumber: 100,
            durationMs: 3000,
          },
        ],
        footnoteHeader: 'Optimizing experience',
        footnote: 'Ready! Finalizing setup...',
        socialProof: {
          count: '300,000 +',
          caption: 'people have chosen Nerva as part of their care.',
        },
      },
      nextScreen: NervaControlScreenName.ProgramOverview,
    },

    {
      screenName: NervaControlScreenName.DownloadAppAccessCode,
      type: ScreenType.DownloadApp,
      layout: {
        title: ['You’re all set'],
        body: ['Your personalized program is ready in the Nerva app'],
        alreadyPaid: true,
        steps: DOWNLOAD_APP_STEPS,
        help: DOWNLOAD_APP_HELP,
      },
    },

    {
      screenName: NervaControlScreenName.ProgramOverview,
      type: ScreenType.ProgramOverview,
      showHeaderBar: true,
      showProgressBar: true,
      layout: {
        title: 'Your personalized program includes:',
        statisticHighlight: '81%',
        statistic: 'of people saw significant improvement in symptom management.¹',
        body: [
          'Over the next 6 weeks, you’ll follow short 15-minute daily sessions designed to help calm an over-sensitive gut–brain connection.',
        ],
        programItems: [
          { icon: icons.volumeHigh, label: 'Gut-directed hypnotherapy' },
          { icon: icons.bookOpen, label: 'CBT-informed coaching' },
          { icon: icons.wind, label: 'Deep breathing techniques' },
          { icon: icons.crosshairs, label: 'Symptom tracking' },
        ],
        footnotes: [
          'In a randomized controlled trial: Anderson et al. (2025), American Journal of Gastroenterology, 120(2), 440–448.',
        ],
        primaryButtonTitle: 'Next',
      },
      ...routeBy(getPaymentPathRoute, {
        // Every US 65+ user goes to the health-cover question first (which then routes by Medicare
        // type). All three 65+ keys map here so a type already in state — e.g. a returning user —
        // can't fall through to `fallback: Payment`. Everyone else collecting insurance (US under-65
        // + non-US such as AU on the standard payment path) → the insurance-provider screen.
        medicareOriginal: MedicareAccessScreenName.MedicareAccessHealthCover,
        medicareAdvantage: MedicareAccessScreenName.MedicareAccessHealthCover,
        medicareOther: MedicareAccessScreenName.MedicareAccessHealthCover,
        collectInsurance: NervaControlScreenName.InsuranceProvider,
        accessCode: NervaControlScreenName.DownloadAppAccessCode,
        asl: NervaControlScreenName.RedirectToPaymentASL,
        pap: PapScreenName.PapPublicAssistance,
        payment: SharedScreenName.Payment,
        fallback: SharedScreenName.Payment,
      }),
    },
    {
      screenName: NervaControlScreenName.InsuranceProvider,
      type: ScreenType.InsuranceProvider,
      layout: {
        question: 'Which insurance provider do you have?',
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        // Keep the referral coupon set from the URL (the 25% HCP discount) rather than overwriting
        // it with the insurance discount.
        applyInsuranceCoupon: false,
        coverageCheck: COVERAGE_CHECK_LAYOUT,
        auCoverageCheck: AU_COVERAGE_CHECK_LAYOUT,
      },
      // A partner insurer gets its no-cost access offer. Other US non-65+ and AU users enter the
      // coverage flow (US Original Medicare skips straight to the address; AU goes to the membership
      // number) unless they chose to self-pay or have no private insurance (AU); everyone else and
      // those users keep their referral route (access code / ASL / PAP / payment).
      ...routeBy(getReferredCoverageEntryRoute, {
        partnerInsurer: CoverageScreenName.UsedPartnerInsurance,
        collectCoverage: CoverageScreenName.CoveragePlanType,
        medicareOriginal: CoverageScreenName.CoverageAddress,
        collectMemberId: CoverageScreenName.CoverageMemberId,
        accessCode: NervaControlScreenName.DownloadAppAccessCode,
        asl: NervaControlScreenName.RedirectToPaymentASL,
        pap: PapScreenName.PapPublicAssistance,
        payment: SharedScreenName.Payment,
        fallback: SharedScreenName.Payment,
      }),
    },
    {
      // Original Medicare enters the ACCESS pathway at the MBI screen (as the dropdown routed
      // before); everyone else re-resolves the referral/payment route. No PAP assistance card
      // (MIS-189): referred users only reach PAP when their referral routes them there.
      screenName: CoverageScreenName.CoverageResults,
      type: ScreenType.CoverageResults,
      layout: COVERAGE_RESULTS_LAYOUT,
      // The check is filed on the consents screen; answers changed after it would never reach it.
      resetHistoryOnEntry: true,
      // Otherwise the long ACCESS branch holds everyone else's bar at about 50% here.
      progressFollowsRoute: true,
      ...routeBy(getReferredCoverageResultsRoute, {
        medicareOriginal: MedicareAccessScreenName.MedicareAccessMBI,
        accessCode: NervaControlScreenName.DownloadAppAccessCode,
        asl: NervaControlScreenName.RedirectToPaymentASL,
        pap: PapScreenName.PapPublicAssistance,
        payment: SharedScreenName.Payment,
        fallback: SharedScreenName.Payment,
      }),
    },
    {
      // Taking the access replaces the referral coupon with the partner's; continuing without it
      // keeps the referral route and coupon.
      screenName: CoverageScreenName.UsedPartnerInsurance,
      type: ScreenType.PartnerInsurerAccess,
      layout: PARTNER_INSURER_ACCESS_LAYOUT,
      ...routeBy(getReferredPartnerInsurerAccessRoute, {
        partnerAccess: SharedScreenName.Payment,
        accessCode: NervaControlScreenName.DownloadAppAccessCode,
        asl: NervaControlScreenName.RedirectToPaymentASL,
        pap: PapScreenName.PapPublicAssistance,
        payment: SharedScreenName.Payment,
        fallback: SharedScreenName.Payment,
      }),
    },
    {
      // Follows the 65+ health-cover Original Medicare answer. Outside an ACCESS state, 65+ skip the
      // Advantage provider question (it lists Advantage plans) and go straight to the no-cost screen.
      screenName: MedicareAccessScreenName.MedicareAccessState,
      type: ScreenType.DropdownSelect,
      layout: MEDICARE_ACCESS_STATE_LAYOUT,
      ...routeBy(getMedicareAccessStateRoute, {
        medicareOriginal: MedicareAccessScreenName.MedicareAccessDisclosure,
        notInAccessState: MedicareAccessScreenName.MedicareAccessAdvantageQualified,
        fallback: SharedScreenName.Payment,
      }),
    },

    {
      screenName: NervaControlScreenName.RedirectToPaymentASL,
      type: ScreenType.ExternalLink,
      layout: {
        content: [
          {
            type: 'text',
            content: 'Your personalized program is ready.',
          },
          {
            type: 'text',
            content: 'Please use the email below to access your Nerva program.',
          },
          { type: 'usersEmailContainer' },
          {
            type: 'text',
            content: 'Once payment is complete, download the Nerva app to get started.',
          },
        ],
        primaryButtonTitle: 'Continue to payment',
        externalUrl: getAslPaymentLink,
      },
    },

    {
      screenName: SharedScreenName.AlreadyPaidDownloadApp,
      type: ScreenType.DownloadApp,
      layout: {
        title: ['You already have access!'],
        body: ['Use the same email and password you signed up with:'],
        alreadyPaid: true,
      },
    },

    {
      screenName: SharedScreenName.Payment,
      type: ScreenType.Payment,
      showHeaderBar: false,
      nextScreen: SharedScreenName.DownloadApp,
    },

    {
      screenName: SharedScreenName.DownloadApp,
      type: ScreenType.DownloadApp,
      layout: {
        title: ['You’re all set'],
        body: ['Your personalized program is ready in the Nerva app'],
        alreadyPaid: false,
        steps: DOWNLOAD_APP_STEPS,
        help: DOWNLOAD_APP_HELP,
      },
    },
    ...PATIENT_ASSISTANCE_SCREENS,
    ...MEDICARE_ACCESS_SCREENS,
    ...COVERAGE_SCREENS,
  ],
};
